// Winner (1x2) — home/draw/away odds table (Phase 3 → M2 wave 9). Structurally
// identical to Match Goals (same bm- classes, value in both layouts), so it
// reuses the shared <MarketTable>; only the rows differ (three outcomes instead
// of goal lines). Home/away labels are team abbreviations resolved from the legacy
// window.TEAM_ABBR (keeping @bento/shared window-free), matching the legacy
// renderer. Cross-module highlight sync stays legacy (Phase 4).
//
// The winner dataset now comes from app-api via `useWidgetData` (was the
// `getWinner1x2Data` legacyData getter); the bookmaker toggle reshapes the fetched
// payload client-side (no refetch).

import { shapeWinnerTable } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import MarketTable from "./MarketTable.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

type TeamAbbr = Record<string, string>;
function teamAbbr(): TeamAbbr {
  return (window as unknown as { TEAM_ABBR?: TeamAbbr }).TEAM_ABBR ?? {};
}

export interface Winner1x2Props {
  fixture: string;
  /** Bookmaker toggle; defaults to "Pinnacle". */
  bookmaker?: string | null;
}

export default function Winner1x2({ fixture, bookmaker }: Winner1x2Props) {
  const fix = fixture || "Arsenal v Everton";
  const { data, isPending, isError, refetch } = useWidgetData(
    "winner-1x2",
    useFixtureKey(fixture)
  );
  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const parts = fix.split(" v ");
  const t1 = (parts[0] ?? "").trim();
  const t2 = (parts[1] ?? "").trim();
  const abbr = teamAbbr();
  // Same fallback as the legacy: TEAM_ABBR lookup, else the first 3 letters upper.
  const labels = {
    home: abbr[t1] || t1.substring(0, 3).toUpperCase(),
    draw: "Draw",
    away: abbr[t2] || t2.substring(0, 3).toUpperCase(),
  };
  const rows =
    shapeWinnerTable(data ?? {}, fix, bookmaker || "Pinnacle", labels) ?? [];
  return <MarketTable wrapperClass="gm-wrapper" rows={rows} />;
}
